import { apiClient } from "../api/apiClient.js";

// Thin fetch wrappers over the Prompt 106 Backoffice Matching Queue API.
// Every query param is optional - visibility scoping (Agent/PLC/SuperAgent/
// SuperAdmin) always happens on the backend from the caller's own session,
// never from a value this file sends. This file only ever calls the three
// real, read-only P106 routes (list ready jobs, one job's detail, one job's
// candidates) - it never calls an assignment/tracking/device/geofence/
// Invite-Card API (none exist anywhere in this codebase), and never sends a
// workerId as a write - the only worker-shaped value this file ever touches
// is a workerId read back from a candidate list response, used purely to
// look up that same candidate's own already-fetched row client-side.

const MATCHING_QUEUE_PATH = "/backoffice/thailand-jobs/matching-queue";

// Exactly the param names BackofficeMatchingQueueController.ListReadyJobs
// declares, in the same order as the controller signature - anything else
// passed to listMatchingQueueJobs is silently dropped rather than
// forwarded, so a typo'd key (or a future filter added to the UI without
// updating this whitelist) can never accidentally reach the backend as an
// unfiltered pass-through.
const JOB_LIST_PARAM_KEYS = [
  "page",
  "pageSize",
  "search",
  "superAgentOrganizationId",
  "plcOrganizationId",
  "agentId",
  "employerId",
  "province",
  "jobStatus",
];

// Exactly the param names BackofficeMatchingQueueController.ListCandidates
// declares. This packet's UI never renders candidate-level filter controls
// (search/nationality/gender/skillCode/skillName/agentId/employerId/
// employerId) - only pagination is ever sent - but the whitelist still
// names every real param so a future filter UI can be added here without
// widening this file's contract silently.
const CANDIDATE_LIST_PARAM_KEYS = [
  "page",
  "pageSize",
  "search",
  "nationality",
  "gender",
  "skillCode",
  "skillName",
  "agentId",
  "employerId",
];

/** Builds a query string with every empty/null/undefined value omitted entirely (never sent as `key=`), restricted to `paramKeys`. */
function buildQuery(paramKeys, params = {}) {
  const searchParams = new URLSearchParams();
  for (const key of paramKeys) {
    const value = params[key];
    if (value === null || value === undefined || value === "") {
      continue;
    }
    searchParams.set(key, String(value));
  }
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

export function listMatchingQueueJobs(params = {}) {
  return apiClient.get(`${MATCHING_QUEUE_PATH}/jobs${buildQuery(JOB_LIST_PARAM_KEYS, params)}`);
}

// Detail fetch requires a real, non-empty jobId - this file never issues a
// request for a missing/blank id (the caller only calls this once a
// row/card is actually clicked).
export function getMatchingQueueJob(jobId) {
  if (!jobId) {
    throw new Error("getMatchingQueueJob requires a jobId");
  }
  return apiClient.get(`${MATCHING_QUEUE_PATH}/jobs/${encodeURIComponent(jobId)}`);
}

// Candidate list requires a real, non-empty jobId - the real endpoint
// (GET .../jobs/{jobId}/candidates) is scoped to exactly one already-ready
// job at a time, never a platform-wide candidate search.
export function listMatchingQueueCandidates(jobId, params = {}) {
  if (!jobId) {
    throw new Error("listMatchingQueueCandidates requires a jobId");
  }
  return apiClient.get(`${MATCHING_QUEUE_PATH}/jobs/${encodeURIComponent(jobId)}/candidates${buildQuery(CANDIDATE_LIST_PARAM_KEYS, params)}`);
}
